import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
export interface FeaturePageProps {
  seoTitle?: string;
  seoDesc?: string;
  heroEyebrow?: string;
  heroTitle?: React.ReactNode;
  heroDesc?: string;
  heroImage?: string;
  hero?: {
    badge?: string;
    h1?: React.ReactNode;
    subtitle?: string;
    stats?: {
      value: string;
      label: string;
    }[];
  };
  customContent?: React.ReactNode;
  problem?: {
    subtitle: string;
    cards: {
      icon: React.ReactNode;
      title: string;
      desc: string;
    }[];
  };
  howItWorks?: {
    title?: string;
    steps: {
      title: string;
      desc: string;
    }[];
  };
  features?: {
    cards: {
      icon: React.ReactNode;
      title: string;
      desc: string;
    }[];
  };
  integrations?: {
    tools: string[];
  };
  pricingCallout?: string;
  demoCallout?: string;
  demoTitle?: string;
  demoDesc?: string;
  firstClientCallout?: string;
}
function AnimatedCounter({
  value
}: {
  value: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true
  });
  const [displayValue, setDisplayValue] = useState('0');
  useEffect(() => {
    if (!isInView) return;
    // If value contains non-numeric characters, just show it
    const numericMatch = value.match(/[\d.]+/);
    if (!numericMatch) {
      setDisplayValue(value);
      return;
    }
    const target = parseFloat(numericMatch[0]);
    const suffix = value.replace(numericMatch[0], '');
    const duration = 2000;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      current += increment;
      if (step >= steps) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(current) + suffix);
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [isInView, value]);
  return <div ref={ref} className="font-poppins font-extrabold text-4xl md:text-5xl text-white" data-id="element-123" data-mp="1o6qejbfuv2q9">
      {displayValue}
    </div>;
}
export function FeaturePageLayout({
  hero,
  heroEyebrow,
  heroTitle,
  heroDesc,
  heroImage,
  seoTitle,
  seoDesc,
  customContent,
  problem,
  howItWorks,
  features,
  integrations,
  pricingCallout,
  demoCallout,
  demoTitle,
  demoDesc,
  firstClientCallout
}: FeaturePageProps) {
  // Support both flat props and hero object
  const badge = heroEyebrow || hero?.badge;
  const h1 = heroTitle || hero?.h1;
  const subtitle = heroDesc || hero?.subtitle;
  const stats = hero?.stats;
  return <main className="pt-20" data-id="element-124" data-mp="2nx13wapo47d1">
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 lg:pt-44 lg:pb-36 px-6 bg-hero-gradient overflow-hidden" data-id="element-125" data-mp="2nxkcv1xcblex">
        {/* Ambient Glow Orbs */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple/20 rounded-full blur-[120px] opacity-30" data-id="element-126" data-mp="tizzlabcms5k" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-teal/20 rounded-full blur-[100px] opacity-20" data-id="element-127" data-mp="yhl0lbyfdfv2" />

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `radial-gradient(circle, white 1px, transparent 1px)`,
        backgroundSize: '40px 40px'
      }} data-id="element-128" data-mp="3bugwiybjp4kp" />

        <div className="inner-container relative z-10 text-center max-w-4xl mx-auto" data-id="element-129" data-mp="2y1hjr435dogu">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-130" data-mp="3uqyo9fqe94gy">
            {badge && <div className="mb-6 flex justify-center" data-id="element-131" data-mp="3grpil9ztdsr8">
                <span className="inline-flex items-center px-5 py-2 rounded-full border border-purple/40 bg-purple/10 text-sm font-poppins font-semibold tracking-wider uppercase text-purple-300" data-id="element-132" data-mp="3gr7fbteqie9t">
                  {badge}
                </span>
              </div>}

            <h1 className="font-poppins font-extrabold text-5xl md:text-6xl lg:text-7xl leading-[1.1] mb-6" data-id="element-133" data-mp="3t86r4dl2bolc">
              {h1}
            </h1>

            <p className="text-lg md:text-xl text-steel mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-134" data-mp="1w5b7bkegwgw">
              {subtitle}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16" data-id="element-135" data-mp="3p6lgkyw03meb">
              <button className="btn-primary shadow-[0_0_40px_rgba(124,58,237,0.3)] hover:shadow-[0_0_50px_rgba(124,58,237,0.5)] transition-shadow" data-id="element-136" data-mp="gr1vin5hhaac">
                Talk to MR
              </button>
            </div>

            {/* Animated Stats Row */}
            {stats && stats.length > 0 && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-12 border-t border-white/10" data-id="element-137" data-mp="bryx3lk0e2bc">
                {stats.map((stat, i) => <div key={i} className="flex flex-col items-center" data-id="element-138" data-mp="2y0ejyyr4uvig" data-mpi={i}>
                    <AnimatedCounter value={stat.value} data-id="element-139" data-mp="2xxikh5zmzyrw" />
                    <div className="text-sm text-steel mt-2 uppercase tracking-wider font-semibold" data-id="element-140" data-mp="2t23awq8nwp1a">
                      {stat.label}
                    </div>
                  </div>)}
              </motion.div>}
          </motion.div>
        </div>
      </section>

      {/* Custom Content Section */}
      {customContent && <section className="relative z-20 -mt-12 mb-24 px-6" data-id="element-141" data-mp="2aak9gnuadj1d">
          <div className="max-w-7xl mx-auto" data-id="element-142" data-mp="2a87ijl6jvyc6">{customContent}</div>
        </section>}

      {/* Problem Section */}
      {problem && <section className="py-24 px-6 bg-dark-bg relative" data-id="element-143" data-mp="1hm6cmbexosre">
          {/* Ambient Glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-purple/10 rounded-full blur-[150px] opacity-40" data-id="element-144" data-mp="3ho5xedones5p" />

          <div className="inner-container relative z-10" data-id="element-145" data-mp="3ml4k4b5zfwtv">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-146" data-mp="3rdfp3sfky8ta">
              <span className="eyebrow" data-id="element-147" data-mp="7egrrn01tbc">The Problem</span>
              <h2 className="h2-title" data-id="element-148" data-mp="6v2qm0gzp2l">{problem.subtitle}</h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 justify-center" data-id="element-149" data-mp="2yq1x88qqo3g1">
              {problem.cards.map((card, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.15
          }} className="group bg-gradient-to-br from-white/[0.05] to-white/[0.02] border border-white/10 rounded-xl p-8 shadow-lg hover:border-purple/40 hover:shadow-[0_0_30px_rgba(124,58,237,0.15)] transition-all duration-300" data-id="element-150" data-mp="33q7in76f0tpo" data-mpi={i}>
                  <div className="text-teal mb-4" data-id="element-151" data-mp="23m6oxtvyl55l">{card.icon}</div>
                  <h3 className="font-poppins font-bold text-xl mb-3" data-id="element-152" data-mp="1yof0j9uiw165">
                    {card.title}
                  </h3>
                  <p className="text-steel leading-relaxed" data-id="element-153" data-mp="1to0o7flw252b">{card.desc}</p>
                </motion.div>)}
            </div>
          </div>
        </section>}

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-teal/30 to-transparent" data-id="element-154" data-mp="1kr9wtgia7ee2" />

      {/* How It Works */}
      {howItWorks && howItWorks.steps.length > 0 && <section className="section-padding bg-dark-bg relative overflow-hidden" data-id="element-155" data-mp="3f2y54zu0ciph">
          <div className="inner-container relative z-10" data-id="element-156" data-mp="3a5oh9om9of3r">
            <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-157" data-mp="2iz9d2rxh47gr">
              <span className="eyebrow" data-id="element-158" data-mp="2izst233kcls5">How It Works</span>
              <h2 className="h2-title" data-id="element-159" data-mp="2nydo7o3ykf9q">Say It. Done.</h2>
            </div>

            <div className="grid md:grid-cols-3 gap-12 relative max-w-5xl mx-auto" data-id="element-160" data-mp="299he7liccs6h">
              {/* Animated Connector Line */}
              <div className="hidden md:block absolute top-16 left-[15%] right-[15%] h-1 overflow-hidden rounded-full" data-id="element-161" data-mp="2e798unadesed">
                <div className="h-full bg-gradient-to-r from-purple via-teal to-purple opacity-30" data-id="element-162" data-mp="206yhlj55fbso" />
                <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent" initial={{
              x: '-100%'
            }} animate={{
              x: '200%'
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'linear'
            }} data-id="element-163" data-mp="24bfxh78e14y6" />
              </div>

              {howItWorks.steps.map((step, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.2
          }} className="relative z-10 flex flex-col items-center text-center" data-id="element-164" data-mp="1919isk2ld76u" data-mpi={i}>
                  {/* Gradient Circle */}
                  <div className="relative w-32 h-32 mb-6" data-id="element-165" data-mp="21pye22gyuiao">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-purple to-teal p-[3px] shadow-[0_0_30px_rgba(124,58,237,0.3)]" data-id="element-166" data-mp="21qgihmds82to">
                      <div className="w-full h-full rounded-full bg-navy-mid flex items-center justify-center" data-id="element-167" data-mp="7c7bfmx20yoq">
                        <span className="text-4xl font-poppins font-bold bg-gradient-to-br from-purple-300 to-teal-300 bg-clip-text text-transparent" data-id="element-168" data-mp="1zj0otr65sre7">
                          {i + 1}
                        </span>
                      </div>
                    </div>
                  </div>

                  <h3 className="font-poppins font-bold text-xl mb-3" data-id="element-169" data-mp="1kli4d10il0ck">
                    {step.title}
                  </h3>
                  <p className="text-steel leading-relaxed" data-id="element-170" data-mp="2i7xl20l49tz2">{step.desc}</p>
                </motion.div>)}
            </div>
          </div>
        </section>}

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-purple/30 to-transparent" data-id="element-171" data-mp="28gukczw3nh0b" />

      {/* Features */}
      {features && features.cards.length > 0 && <section className="section-padding bg-navy-dark relative overflow-hidden" data-id="element-172" data-mp="310057lwyvse2">
          {/* Ambient Glow */}
          <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-teal/10 rounded-full blur-[120px] opacity-30" data-id="element-173" data-mp="2w7gj20k7roxn" />

          <div className="inner-container relative z-10" data-id="element-174" data-mp="34iyz9bvsjobl">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-175" data-mp="38jiuub0pujvm">
              <span className="eyebrow" data-id="element-176" data-mp="3dhu3wjj6sqcs">Capabilities</span>
              <h2 className="h2-title" data-id="element-177" data-mp="3dg98yssyn5ee">Everything You Need.</h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 justify-center" data-id="element-178" data-mp="3ihgfypna40k0">
              {features.cards.map((card, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="group relative bg-gradient-to-br from-white/[0.05] to-white/[0.02] border border-white/10 rounded-xl p-6 hover:border-purple/40 transition-all duration-300 overflow-hidden" data-id="element-179" data-mp="3ndxg6i7r65dg" data-mpi={i}>
                  {/* Shimmer Effect on Hover */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-180" data-mp="3v6qge3mc47ye">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" data-id="element-181" data-mp="3qg09un269z24" />
                  </div>

                  <div className="relative z-10" data-id="element-182" data-mp="32egjntoidak">
                    <div className="flex items-center gap-4 mb-4" data-id="element-183" data-mp="i0goea7qtkxm">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple/20 to-teal/20 flex items-center justify-center text-purple group-hover:scale-110 transition-transform duration-300" data-id="element-184" data-mp="hzoy9ewx0feq">
                        {card.icon}
                      </div>
                      <h3 className="font-poppins font-bold text-lg" data-id="element-185" data-mp="2ets3zwloyx9r">
                        {card.title}
                      </h3>
                    </div>
                    <p className="text-steel text-sm leading-relaxed" data-id="element-186" data-mp="z9suoeqhrhqa">
                      {card.desc}
                    </p>
                  </div>
                </motion.div>)}
            </div>

            {/* Callouts */}
            {(pricingCallout || demoCallout || firstClientCallout || integrations) && <div className="mt-16 grid gap-6 max-w-4xl mx-auto" data-id="element-187" data-mp="bmb3olf1wnod">
                {integrations && <div className="bg-gradient-to-br from-white/[0.05] to-white/[0.02] border border-teal/30 rounded-xl p-8 text-center relative overflow-hidden" data-id="element-188" data-mp="fjguy6rr4zyb">
                    <div className="absolute inset-0 bg-teal/5 blur-xl" data-id="element-189" data-mp="6lwtoxtw33np" />
                    <div className="relative z-10" data-id="element-190" data-mp="1vzs5kfu46sc1">
                      <h4 className="font-poppins font-bold text-lg mb-6" data-id="element-191" data-mp="1vxyveqkalpza">
                        Works With Your Stack
                      </h4>
                      <div className="flex flex-wrap justify-center gap-3" data-id="element-192" data-mp="x57myb9u00b">
                        {integrations.tools.map(tool => <span key={tool} className="px-5 py-2.5 rounded-full bg-dark-bg border border-white/10 text-sm font-medium text-steel hover:border-teal/40 hover:shadow-[0_0_20px_rgba(7,133,183,0.15)] transition-all duration-300" data-id="element-193" data-mp="3s3zkdtfc8sx8">
                            {tool}
                          </span>)}
                      </div>
                    </div>
                  </div>}

                {pricingCallout && <div className="bg-purple/10 border border-purple/30 rounded-xl p-6 text-center" data-id="element-194" data-mp="30ck1a9czpobr">
                    <p className="text-purple-alt font-medium" data-id="element-195" data-mp="2vdgt4g1pr1zk">
                      {pricingCallout}
                    </p>
                  </div>}

                {demoCallout && <div className="bg-teal/10 border border-teal/30 rounded-xl p-6 text-center" data-id="element-196" data-mp="15wlbasgm70bj">
                    <p className="text-teal font-medium" data-id="element-197" data-mp="1audbe9rexsqt">💡 {demoCallout}</p>
                  </div>}

                {firstClientCallout && <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center" data-id="element-198" data-mp="1fs5599bmboxn">
                    <p className="text-white/80 font-medium" data-id="element-199" data-mp="1jtguveuhsa5u">
                      🏆 {firstClientCallout}
                    </p>
                  </div>}
              </div>}
          </div>
        </section>}

      {/* Gradient Divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-purple/30 to-transparent" data-id="element-200" data-mp="w4fmtspkn113" />

      {/* Final CTA */}
      <section className="section-padding bg-hero-gradient relative overflow-hidden" data-id="element-201" data-mp="10ytm467ctsb1">
        {/* Animated Gradient Mesh Background */}
        <div className="absolute inset-0" data-id="element-202" data-mp="8fwbt5f50a5b">
          <div className="absolute top-0 left-0 w-full h-full bg-dark-bg/50" data-id="element-203" data-mp="8ev152219po4" />
          <motion.div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple/30 rounded-full blur-[100px]" animate={{
          x: [0, 50, 0],
          y: [0, 30, 0]
        }} transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-204" data-mp="1bqa7vvmoq1qq" />
          <motion.div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal/20 rounded-full blur-[100px]" animate={{
          x: [0, -40, 0],
          y: [0, -50, 0]
        }} transition={{
          duration: 10,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-205" data-mp="ss7n5t166qyq" />
        </div>

        {/* Floating Particles */}
        {[...Array(4)].map((_, i) => <motion.div key={i} className="absolute w-2 h-2 bg-white/20 rounded-full" style={{
        left: `${20 + i * 20}%`,
        top: `${30 + i * 15}%`
      }} animate={{
        y: [0, -30, 0],
        opacity: [0.2, 0.5, 0.2]
      }} transition={{
        duration: 3 + i,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: i * 0.5
      }} data-id="element-206" data-mp="2d6q5y8jxkcc1" data-mpi={i} />)}

        <div className="inner-container relative z-10 text-center max-w-3xl mx-auto" data-id="element-207" data-mp="2bt8b323qdpvf">
          <h2 className="h2-title" data-id="element-208" data-mp="2zhhyu9ki8np7">Ready to get your life back?</h2>
          <p className="text-xl text-steel mb-10" data-id="element-209" data-mp="34ernvo3zexci">
            MIndSTReam.ing — Your business, running itself.
          </p>
          <button className="btn-primary text-lg px-10 py-5 shadow-[0_0_40px_rgba(124,58,237,0.4)] hover:shadow-[0_0_60px_rgba(124,58,237,0.6)] transition-shadow" data-id="element-210" data-mp="2pqn1a91t61og">
            Talk to MR Now →
          </button>
          <p className="mt-6 text-sm text-steel" data-id="element-211" data-mp="3idhld4upqz1e">
            No contracts. No setup fees. Strategy call included.
          </p>
        </div>
      </section>
    </main>;
}